import { Button, Card, Col, DatePicker, Row, Spin } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import { EventReportKeyColumnDescription } from 'src/api/api'
import { useUser } from 'src/context/UserContext'
import { formatCompact } from 'src/util/Util'
import { PerformanceData, Period } from './useDashboardData'

const { RangePicker } = DatePicker
const startFormat = 'YYYY-MM-DD 00:00:00'
const endFormat = 'YYYY-MM-DD 23:00:00'
const KeyColumnEnum = EventReportKeyColumnDescription.ColumnEnum

interface Props {
    period: Period
    setPeriod: (value: Period) => void
    data?: PerformanceData
    loading?: boolean
}

const now = dayjs()

const DashboardHeader: React.FC<Props> = ({ period, setPeriod, data, loading }) => {

    const { blackTheme } = useUser()

    const [showPicker, setShowPicker] = useState(false)
    const [customRange, setCustomRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>()

    const predefinedFilters = useMemo(() => [
        {
            label: 'Today',
            range: () => {
                return { key: KeyColumnEnum.DayHour, dates: [now.format(startFormat), now.format(endFormat)] }
            }
        },
        {
            label: 'Yesterday',
            range: () => {
                const yesterday = now.subtract(1, 'day')
                return { key: KeyColumnEnum.DayHour, dates: [yesterday.format(startFormat), yesterday.format(endFormat)] }
            }
        },
        {
            label: 'Last 7 Days',
            range: () => {
                const start = now.subtract(7, 'day')
                return { key: KeyColumnEnum.Day, dates: [start.format(startFormat), now.format(endFormat)] }
            }
        },
        {
            label: 'Last 28 Days',
            range: () => {
                const start = now.subtract(27, 'day')
                return { key: KeyColumnEnum.Day, dates: [start.format(startFormat), now.format(endFormat)] }
            }
        },
        {
            label: 'Last Month',
            range: () => {
                const start = now.subtract(1, 'month').startOf('month')
                const end = now.subtract(1, 'month').endOf('month')
                return { key: KeyColumnEnum.Day, dates: [start.format(startFormat), end.format(endFormat)] }
            }
        },
        {
            label: '+ Custom',
            range: () => {
                const [start, end] = customRange || [now.startOf('day'), now.endOf('day')]
                const diff = end.diff(start, 'day')
                return { key: diff <= 1 ? KeyColumnEnum.DayHour : KeyColumnEnum.Day, dates: [start.format(startFormat), end.format(endFormat)] }
            }
        }
    ], [customRange])

    const isSameRange = (a: Period, b: Period) => a.key === b.key && a.dates[0] === b.dates[0] && a.dates[1] === b.dates[1]

    useEffect(() => {
        if (showPicker && customRange) {
            const [start, end] = period.dates
            const [startCustom, endCustom] = customRange
            if (start !== startCustom.format(startFormat) || end !== endCustom.format(endFormat)) {
                setCustomRange([dayjs(start), dayjs(end)])
            }
        }
    }, [period, showPicker, customRange])

    // TODO: move outside
    const stats = [
        { title: 'Impressions', value: data?.impressionSum, toUnits: undefined },
        { title: 'Clicks', value: data?.clickSum, toUnits: undefined },
        { title: 'Cost', value: data?.costSum, toUnits: v => '$' + v },
        { title: 'CTR', value: data?.ctrAvg, toUnits: v => v + '%' },
        { title: 'eCPM', value: data?.ecpmAvg, toUnits: v => '$' + v }
    ]

    return (
        <Card style={{ borderRadius: 8, marginBottom: 8 }} bodyStyle={{ padding: 24 }}>
            <Row gutter={[2, 16]} style={{ marginBottom: 24 }} wrap justify="start">
                {predefinedFilters.map(({ label, range }) => {

                    const rangeValue = range()
                    const isActive = label === '+ Custom' ? customRange != undefined : isSameRange(period, rangeValue)

                    return (
                        <Col key={label}>
                            <Button
                                style={{ paddingLeft: 8, paddingRight: 8 }}
                                type={isActive ? 'primary' : 'text'}
                                className="responsive-button"
                                onClick={() => {
                                    if (label === '+ Custom') {
                                        setShowPicker(true)
                                    } else {
                                        setShowPicker(false)
                                        setCustomRange(undefined)

                                        setPeriod(rangeValue)
                                    }
                                }}
                                ghost
                            >
                                <span>{label}</span>
                            </Button>
                        </Col>
                    )
                })}
                {showPicker && (
                    <Col>
                        <RangePicker
                            value={customRange}
                            allowClear={false}
                            onChange={(dates) => {
                                if (dates && dates[0] && dates[1]) {

                                    const start = dates[0].startOf('day')
                                    const end = dates[1].endOf('day')

                                    setCustomRange([start, end])

                                    const diff = end.diff(start, 'day') + 1
                                    setPeriod({
                                        key: diff <= 1 ? KeyColumnEnum.DayHour : KeyColumnEnum.Day,
                                        dates: [start.format(startFormat), end.format(endFormat)]
                                    })
                                }
                            }}
                        />
                    </Col>
                )}
            </Row>
            <Row gutter={[16, 16]} justify="space-between">
                {loading ? <Spin size="large" style={{
                    display: 'block',
                    margin: '20px auto',
                    minHeight: '100px',
                    alignContent: 'center'
                }} /> :
                    stats.map((stat) => (
                        <Col flex="1 0 0%" key={stat.title}>
                            <Card
                                style={{
                                    background: blackTheme ? '#232E3F' : '#F2F7FF',
                                    borderRadius: 6,
                                    textAlign: 'left',
                                    height: '100%',
                                    paddingLeft: 12,
                                    paddingTop: 8,
                                    paddingBottom: 16,
                                    border: 'transparent'
                                }}
                                bodyStyle={{ padding: 0, height: '100%' }}
                            >
                                <div>{stat.title}</div>
                                <div style={{ fontSize: 24, fontWeight: 700, paddingTop: 12 }}>
                                    {formatCompact(stat.value || 0, stat.toUnits)}
                                </div>
                            </Card>
                        </Col>
                    ))}
            </Row>
        </Card>
    )
}

export default DashboardHeader


// WEBPACK FOOTER //
// ./src/component/app/dashboard/DashboardHeader.tsx